import React, { useState } from 'react'
import {
  Button,
  Icon,
  Popover,
  PopoverList,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import {
  selectDisplayStatus,
  selectSaleSyncStatus,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  GIFT_RECEIPT_STATUSES,
  SaleSyncStatus,
} from '../../../models/sale.constants'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { usePrintReceipt } from './usePrintReceipt'
import { ReceiptTemplateList, SelectReceiptParams } from './ReceiptTemplateList'
import { useReceipts } from './useReceipts'

export const PrintButtons = () => {
  const printReceipt = usePrintReceipt()
  const syncStatus = useAppSelector(selectSaleSyncStatus)
  const displayStatus = useAppSelector(selectDisplayStatus)
  const { otherReceipts } = useReceipts()

  const [otherOpen, setOtherOpen] = useState(false)

  const toggleOtherOpen = () => {
    setOtherOpen(!otherOpen)
  }

  const disabled = syncStatus === SaleSyncStatus.UPLOAD_FIRST_SUBMIT

  const canPrintGiftReceipt =
    getFeatureFromCache('gift_receipts') &&
    displayStatus &&
    GIFT_RECEIPT_STATUSES.includes(displayStatus)

  const printGift = () => {
    printReceipt({ type: 'gift' })
  }

  const handleReceiptSelect = (params: SelectReceiptParams) => {
    setOtherOpen(false)
    if (params.type === 'default') {
      printReceipt()
    } else {
      printReceipt({
        receiptTemplateId: params.receiptTemplateId,
        defaultTemplate: false,
      })
    }
  }

  return (
    <div className="vd-mt5 vd-btn-group">
      <Button
        onClick={() => printReceipt()}
        disabled={disabled}
        variant="supplementary"
        modifier="text"
        aria-label="Print default receipt"
        data-testid="printDefaultReceipt"
        data-track="print-default-receipt"
      >
        <Icon icon="fa-print" className="sale-summary-print-receipt-icon" />
        <span className="sale-summary-print-receipt-label">
          &nbsp;
          <T _str="Print" _context="Print receipt button label" />
        </span>
      </Button>
      {canPrintGiftReceipt && (
        <Button
          onClick={printGift}
          disabled={disabled}
          variant="supplementary"
          modifier="text"
          aria-label="Print gift receipt"
          data-track="print-gift-receipt"
        >
          <Icon icon="fa-gift" className="sale-summary-print-receipt-icon" />
          <span className="sale-summary-print-receipt-label">
            &nbsp;
            <T _str="Gift receipt" _context="Print gift card receipt" />
          </span>
        </Button>
      )}
      {otherReceipts.length > 0 ? (
        <WithPopover>
          <Button
            onClick={toggleOtherOpen}
            variant="supplementary"
            modifier="text"
            aria-label="Print other receipt"
            data-track="print-other-receipt"
          >
            <Icon icon="fa-print" className="sale-summary-print-receipt-icon" />
            &nbsp;
            <span className="sale-summary-print-receipt-label">
              <T
                _str="Other..."
                _context="Button to show other available receipt templates"
              />
            </span>
          </Button>
          {otherOpen && (
            <Popover
              onClose={() => setOtherOpen(false)}
              className="wr-receipt-selection-popover"
            >
              <PopoverList>
                <ReceiptTemplateList selectReceipt={handleReceiptSelect} />
              </PopoverList>
            </Popover>
          )}
        </WithPopover>
      ) : null}
    </div>
  )
}
